/**
 * WealthDistribution — B12: the owner's wealth split by asset class.
 *
 * Groups every §16 asset by type and renders an inline-SVG donut of the value
 * split (NO charting library). The wire value is CENTS (`AssetView.valueZar`);
 * we sum in cents and render whole Rand through the canonical formatter
 * (`formatRandFromCents`), so grouping is the app-standard "R 1 150 000" and
 * never a bare "1150000".
 *
 * POR-02 (M1 §H) — IT READS THE WHOLE ESTATE, NOT ONE TRUST.
 * It used to read `useTrustAssets(trustId)`, and the dashboard passes it
 * `trusts[0]`. So a client with three trusts got a "wealth picture" of whichever
 * trust happened to sort first, under a card headed "How the estate is held".
 * Paul asked for "a pie chart of combined asset values (properties, shares,
 * business interests)"; combined means all of them. `useEstateAssets` is the
 * tenant-wide read that already backs the estate card two rows up, so this is
 * one more consumer of a query that is already in the cache, not a new call.
 *
 * The per-firm visibility scope rides along for free: under a firm act-as
 * session whose client withheld their asset list, the backend omits the
 * `person_owned` rows, so the donut simply shows the trust-held classes.
 *
 * Honest empty state: with no valued assets yet the card shows an inviting muted
 * placeholder, and now a way out of it — the entry point POR-02 also asked for.
 */

import { Link } from '@tanstack/react-router';
import { useEstateAssets } from '../../lib/api';
import { formatRandFromCents } from '../../lib/fmt-r';
import { Skeleton } from '../ui/skeleton';
import { buildWealthSlices, type WealthSlice } from './wealth-distribution-model';

/** Inline-SVG donut — one stroked arc per slice, no library. */
function WealthDonut({ slices, total }: { slices: WealthSlice[]; total: number }) {
  const size = 132;
  const stroke = 20;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  let cum = 0;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label="Wealth distribution by asset class"
      data-testid="wealth-donut"
      style={{ flexShrink: 0 }}
    >
      {/* Track */}
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={stroke} />
      {slices.map((s) => {
        const dash = circ * (s.valueCents / total);
        const arc = (
          <circle
            key={s.type}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-cum}
            transform={`rotate(-90 ${c} ${c})`}
            strokeLinecap="butt"
          />
        );
        cum += dash;
        return arc;
      })}
    </svg>
  );
}

/** Honest, inviting empty state — never a blank panel.
 *
 *  POR-02: it used to point nowhere. "Add assets" was the second half of the
 *  ask and the card that says "as you add assets" is the obvious place to
 *  offer it. The link is omitted when there is no trust to add one to, rather
 *  than rendered dead. */
function EmptyWealth({ addAssetHref }: { addAssetHref?: string }) {
  return (
    <div
      data-testid="wealth-empty"
      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 2px' }}
    >
      <svg width="60" height="60" viewBox="0 0 60 60" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.55 }}>
        <circle cx="30" cy="30" r="21" fill="none" stroke="var(--hairline)" strokeWidth="9" strokeDasharray="4 6" />
      </svg>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        Your wealth picture appears here as you add assets: property, vehicles,
        investments and more, split by class.{' '}
        {addAssetHref ? (
          <Link
            to={addAssetHref as never}
            data-testid="wealth-empty-add-asset"
            style={{
              color: 'var(--primary-700)',
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            Add an asset
          </Link>
        ) : null}
      </p>
    </div>
  );
}

export function WealthDistribution({ trustId }: { trustId: string | undefined }) {
  const { data, isLoading, isError } = useEstateAssets();
  const addAssetHref = trustId
    ? `/app/trusts/${encodeURIComponent(trustId)}/assets`
    : undefined;
  const assets = data?.assets ?? [];
  const slices = buildWealthSlices(assets);
  const total = slices.reduce((sum, s) => sum + s.valueCents, 0);

  return (
    <div className="card" data-testid="wealth-distribution">
      <div className="card-head">
        <div>
          <div className="eyebrow">Wealth</div>
          <h2 className="card-title">How the estate is held</h2>
        </div>
        {total > 0 && (
          <span className="card-meta">
            {formatRandFromCents(total, { cents: false })} · {slices.length}{' '}
            {slices.length === 1 ? 'class' : 'classes'}
          </span>
        )}
      </div>

      {isLoading ? (
        // Shaped shimmer matching the final donut + legend layout — replaces
        // the "Loading your wealth picture…" doubt-words (Paul, 2026-07-15).
        <div
          data-testid="wealth-skeleton"
          style={{ display: 'flex', alignItems: 'center', gap: 22, padding: '4px 0 2px' }}
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading wealth distribution"
          role="status"
        >
          <Skeleton width={132} height={132} radius="50%" style={{ flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 180 }}>
            <Skeleton width="70%" height={13} />
            <Skeleton width="55%" height={13} />
            <Skeleton width="62%" height={13} />
          </div>
        </div>
      ) : isError ? (
        <p style={{ padding: '8px 0', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
          Your wealth picture isn&rsquo;t available just now. Your data is safe.
        </p>
      ) : slices.length === 0 || total === 0 ? (
        <EmptyWealth {...(addAssetHref ? { addAssetHref } : {})} />
      ) : (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', padding: '4px 0 2px' }}
        >
          <WealthDonut slices={slices} total={total} />
          <div
            data-testid="wealth-legend"
            style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 180 }}
          >
            {slices.map((s) => {
              const pct = Math.round((s.valueCents / total) * 100);
              return (
                <div
                  key={s.type}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
                >
                  <span
                    aria-hidden="true"
                    style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }}
                  />
                  <span style={{ color: 'var(--ink-2)' }}>{s.label}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      color: 'var(--ink)',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatRandFromCents(s.valueCents, { cents: false })}
                  </span>
                  <span
                    style={{
                      color: 'var(--ink-4)',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: 34,
                      textAlign: 'right',
                    }}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
